import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import type { Employer } from '../../report-requests.types';

export interface SelectedEmployerSummary {
  employerId: string;
  employerName: string;
  connections: Array<{ connectionId: string; name: string }>;
}

interface ConfirmDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  selectAll: boolean;
  isLoading: boolean;
  showConnections: boolean;
  selections: Record<string, string[]>;
  employers: Employer[];
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onConfirm,
  onCancel,
  selectAll,
  isLoading,
  showConnections,
  selections,
  employers,
}) => {
  const selectedEmployersSummary: SelectedEmployerSummary[] = useMemo(() => {
    if (selectAll) {
      return employers.map(emp => ({
        employerId: emp.employerId,
        employerName: emp.employerName,
        connections: emp.connections.map(c => ({ connectionId: c.connectionId, name: c.name })),
      }));
    }

    return Object.entries(selections).map(([employerId, connectionIds]) => {
      const employer = employers.find(e => e.employerId === employerId);
      const connections = connectionIds
        .map(connId => {
          const conn = employer?.connections.find(c => c.connectionId === connId);
          return conn ? { connectionId: conn.connectionId, name: conn.name } : null;
        })
        .filter((c): c is { connectionId: string; name: string } => c !== null);

      return {
        employerId,
        employerName: employer?.employerName ?? employerId,
        connections,
      };
    });
  }, [selectAll, selections, employers]);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        <FormattedMessage id="reports.confirmationDialog.title" />
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>
          <FormattedMessage id="reports.confirmationDialog.header" />
        </Typography>

        <Typography sx={{ mb: 2 }}>
          <FormattedMessage id="reports.confirmationDialog.confirmationText" />
        </Typography>

        <Typography sx={{ fontWeight: 600, mb: 1 }}>
          <FormattedMessage id="reports.confirmationDialog.selectedLength" />{' '}
          <span>{selectedEmployersSummary.length}</span>
        </Typography>

        {showConnections ? (
          selectedEmployersSummary.map(emp => (
            <Accordion key={emp.employerId} disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ flexDirection: 'row-reverse', gap: 1 }}>
                <Typography sx={{ fontWeight: 500 }}>
                  {emp.employerName}
                  <Typography component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                    {'('}
                    {emp.connections.length}
                    {')'}
                  </Typography>
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pl: 4 }}>
                {emp.connections.length > 0 ? (
                  <List dense disablePadding>
                    {emp.connections.map(conn => (
                      <ListItem key={conn.connectionId} disablePadding sx={{ py: 0.25 }}>
                        <Typography variant="body2">{conn.name}</Typography>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    <FormattedMessage id="global.noData" />
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          ))
        ) : (
          <List dense disablePadding>
            {selectedEmployersSummary.map(emp => (
              <ListItem key={emp.employerId} disablePadding sx={{ py: 0.25 }}>
                <Typography>{emp.employerName}</Typography>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} variant="outlined">
          <FormattedMessage id="no" />
        </Button>
        <Button onClick={onConfirm} variant="contained" disabled={isLoading}>
          <FormattedMessage id="yes" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
